import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  redirect,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { FullyBookedHold } from "@/components/site/FullyBookedHold";
import { WhatsAppBubble } from "@/components/site/WhatsAppBubble";
import { CookieConsentBanner } from "@/components/site/CookieConsentBanner";
import { LOCALE_CODES } from "@/lib/locales/types";
import { SITE_FULLY_BOOKED_HOLD } from "@/lib/site-hold";

const LazyToaster = lazy(() =>
  import("@/components/ui/sonner").then((module) => ({ default: module.Toaster })),
);


function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

const siteTitle = "Car Rental Bucharest — No Deposit, No Credit Card";
const siteDescription =
  "Rent a car in Bucharest with zero deposit and no credit card. Full insurance and rovinieta included, free delivery in the city and at Otopeni Airport.";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  beforeLoad: ({ location }) => {
    if (SITE_FULLY_BOOKED_HOLD && location.pathname !== "/") {
      throw redirect({ to: "/", replace: true });
    }
  },
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "author", content: "Car Rental Bucharest" },
      {
        name: "google-site-verification",
        content: "7qHzvlAMcEI0jLTrycHjpR7Ds7BSAPpjVgfBfN9uTlc",
      },

      { title: siteTitle },
      { name: "description", content: siteDescription },
      { property: "og:site_name", content: "carentals.ro" },
      { property: "og:title", content: siteTitle },
      { property: "og:description", content: siteDescription },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: siteTitle },
      { name: "twitter:description", content: siteDescription },
    ],
    links: [
      {
        rel: "stylesheet",
        href: appCss,
      },
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico?v=3", sizes: "48x48" },
      { rel: "icon", type: "image/svg+xml", href: "/logo.svg?v=3" },
      { rel: "icon", type: "image/png", sizes: "64x64", href: "/favicon.png?v=3" },
      { rel: "icon", type: "image/png", sizes: "512x512", href: "/icon-512.png?v=3" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png?v=3" },
      { rel: "shortcut icon", href: "/favicon.ico?v=3" },
    ],
    scripts: [
      {
        children: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', 'G-YQ1YPRQ8N1');`,
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Organization",
              "@id": "https://carentals.ro/#organization",
              name: "Car Rental Bucharest",
              url: "https://carentals.ro",
              telephone: "+40 738 792 499",
              address: {
                "@type": "PostalAddress",
                streetAddress: "Bulevardul Regina Elisabeta",
                addressLocality: "Bucharest",
                addressCountry: "RO",
              },
            },
            {
              "@type": "WebSite",
              "@id": "https://carentals.ro/#website",
              url: "https://carentals.ro",
              name: "Car Rental Bucharest",
              publisher: { "@id": "https://carentals.ro/#organization" },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const segment = pathname.split("/")[1] ?? "";
  const lang = segment === "ro" || (LOCALE_CODES as readonly string[]).includes(segment) ? segment : "en";
  const dir = segment === "he" ? "rtl" : "ltr";

  return (
    <html lang={lang} dir={dir}>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [mountToaster, setMountToaster] = useState(false);

  useEffect(() => {
    const mount = () => setMountToaster(true);
    const timer = window.setTimeout(mount, 5000);
    window.addEventListener("pointerdown", mount, { once: true, passive: true });
    window.addEventListener("keydown", mount, { once: true });
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("pointerdown", mount);
      window.removeEventListener("keydown", mount);
    };
  }, []);

  useEffect(() => {
    const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
    gtag?.("event", "page_view", { page_path: pathname });
  }, [pathname]);

  // Load Google Analytics after the page is interactive so it never blocks LCP/TBT.
  useEffect(() => {
    if (document.getElementById("ga-tag")) return;
    let done = false;
    const load = () => {
      if (done) return;
      done = true;
      const s = document.createElement("script");
      s.id = "ga-tag";
      s.async = true;
      s.src = "https://www.googletagmanager.com/gtag/js?id=G-YQ1YPRQ8N1";
      document.head.appendChild(s);
      events.forEach((e) => window.removeEventListener(e, load));
    };
    const events = ["scroll", "pointerdown", "keydown", "touchstart"] as const;
    events.forEach((e) => window.addEventListener(e, load, { once: true, passive: true }));
    const timer = window.setTimeout(load, 10000);
    return () => {
      window.clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, load));
    };
  }, []);





  if (SITE_FULLY_BOOKED_HOLD) {
    return (
      <QueryClientProvider client={queryClient}>
        <FullyBookedHold />
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <>
        <div className="flex min-h-screen flex-col">
          <Header />
          <main className="flex-1">
            {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
            <Outlet />
          </main>
          <Footer />
        </div>
        <WhatsAppBubble />
        <CookieConsentBanner />
        {mountToaster ? (
          <Suspense fallback={null}>
            <LazyToaster position="top-center" richColors />
          </Suspense>
        ) : null}
      </>
    </QueryClientProvider>
  );
}

